import { Admin, AdminLocationEmails } from '../../../../clients/quipli-service/admin/admin';
import { Typography } from '../../../../components/typography/typography';
import { toast } from '../../../../util/toasts';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { useCallback, useEffect, useState } from 'react';
import { Toggle } from '../../../../components/form/toggle';
import { Checkbox } from '../../../../components/checkbox/checkbox';
import { debounce, omit } from 'lodash';
import { PermissionGroup, permissionsGroups, permissionsNames } from '../../config/permissions';
import { useAdmin } from '../../../../hooks/use-admin-context';
import { Location } from '../../../../clients/quipli-service/location/location';
import { useAdminApiV1 } from '../../../../clients/quipli-service/admin/admin-api-v1';

export const AdminUserPermissions = ({ adminUser }: { adminUser: Admin }) => {
  const { listLocations } = useAPI();
  const {
    updateUserAdminPermissions,
    getUserAdminPermissions,
    listAdminLocations,
    updateAdminUserLocations,
    getUserAdminEmailNotifications,
    updateUserAdminEmailNotifications,
  } = useAdminApiV1();
  const adminLogged = useAdmin();
  const disableUpdate = adminLogged.id === adminUser.adminId;

  const [isLoading, setIsLoading] = useState(false),
    [isUpdated, setIsUpdate] = useState(false),
    [isLocationUpdated, setIsLocationUpdate] = useState(false),
    [adminPermissions, setAdminPermissions] = useState<string[]>([]),
    [isSelectAllPermissions, setSelectAllPermissions] = useState<boolean>(false),
    [isSelectAllLocations, setSelectAllLocations] = useState<boolean>(false),
    [locations, setLocations] = useState<Location[]>([]),
    [adminLocations, setAdminLocations] = useState<number[]>([]),
    [adminEmails, setAdminEmails] = useState<AdminLocationEmails>({});

  const updatePermissionList = (permission: string, isSelected: boolean) => {
    if (disableUpdate) {
      return;
    }
    setIsUpdate(true);
    if (isSelected) {
      setAdminPermissions((prevState) => {
        return [...prevState, permission];
      });
    } else {
      setAdminPermissions(adminPermissions.filter((item) => item !== permission));
      setSelectAllPermissions(false);
    }
  };

  const updateLocationList = (locationId: number, isSelected: boolean) => {
    if (disableUpdate) {
      return;
    }
    setIsLocationUpdate(true);
    if (isSelected) {
      setAdminLocations((prevState) => {
        return [...prevState, locationId];
      });
    } else {
      setAdminLocations(adminLocations.filter((item) => item !== locationId));

      if (adminEmails[locationId]) {
        setAdminEmails((prevState) => {
          return omit(prevState, locationId);
        });
      }

      setSelectAllLocations(false);
    }
  };

  const selectAllPermissions = async () => {
    if (disableUpdate) {
      return;
    }
    setIsUpdate(true);
    if (!isSelectAllPermissions) {
      setAdminPermissions(() => {
        return permissionsNames;
      });
    } else {
      setAdminPermissions([]);
    }

    setSelectAllPermissions(!isSelectAllPermissions);
  };

  const selectAllLocations = async () => {
    if (disableUpdate) {
      return;
    }
    setIsLocationUpdate(true);
    if (!isSelectAllLocations) {
      setAdminLocations(() => {
        return locations.map((location) => location.id);
      });
    } else {
      setAdminLocations([]);
      setAdminEmails({});
    }

    setSelectAllPermissions(!isSelectAllPermissions);
  };

  const saveAdminPermissions = useCallback(
    debounce(function (permissions: string[]) {
      if (disableUpdate) {
        return;
      }

      setIsLoading(true);
      updateUserAdminPermissions(adminUser.adminId, permissions)
        .then(() => {
          toast('Permissions updated successfully');
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }, 600),
    [],
  );

  const saveAdminLocations = useCallback(
    debounce(function (locations: number[]) {
      if (disableUpdate) {
        return;
      }

      setIsLoading(true);
      updateAdminUserLocations(adminUser.adminId, locations)
        .then(() => {
          toast('Locations updated successfully');
          setIsLoading(false);
        })
        .catch((err) => {
          console.error(err);
          toast('Something wrong updating locations permissions', 'error');
          setIsLoading(false);
        });
    }, 600),
    [],
  );

  //Update only when a location is deselected
  const saveAdminEmailsNotification = useCallback(
    debounce(function (locationsEmails: AdminLocationEmails) {
      if (disableUpdate) {
        return;
      }

      updateUserAdminEmailNotifications(adminUser.adminId, locationsEmails);
    }, 600),
    [],
  );

  const verifyAllPermissionsSelected = (permissionsSelected: string[]) => {
    let isAllSelected = true;
    permissionsNames.map((permission: string) => {
      if (!permissionsSelected.includes(permission)) {
        isAllSelected = false;
        return;
      }
    });
    setSelectAllPermissions(isAllSelected);
  };

  const verifyAllLocationsSelected = (locations: Location[], adminLocationIds: number[]) => {
    let isAllLocationSelected = true;
    locations.map((location: Location) => {
      if (!adminLocationIds.includes(location.id)) {
        isAllLocationSelected = false;
        return;
      }
    });
    setSelectAllLocations(isAllLocationSelected);
  };

  const init = () => {
    setIsLoading(true);

    Promise.all([
      listLocations(true),
      getUserAdminPermissions(adminUser.adminId),
      listAdminLocations(adminUser.adminId),
      getUserAdminEmailNotifications(adminUser.adminId),
    ]).then(([locations, adminPermissions, adminLocation, emails]) => {
      setAdminPermissions(adminPermissions);
      verifyAllPermissionsSelected(adminPermissions);

      setLocations(locations);

      const adminLocationIds = adminLocation.map((location: Location) => {
        return location.id;
      });
      setAdminLocations(adminLocationIds);
      verifyAllLocationsSelected(locations, adminLocationIds);
      setAdminEmails(emails);

      setIsLoading(false);
    });
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (!isLoading && isUpdated && !disableUpdate) {
      saveAdminPermissions(adminPermissions);
      verifyAllPermissionsSelected(adminPermissions);
    }
  }, [adminPermissions]);

  useEffect(() => {
    if (!isLoading && isLocationUpdated && !disableUpdate) {
      saveAdminLocations(adminLocations);
      verifyAllLocationsSelected(locations, adminLocations);
    }
  }, [adminLocations]);

  useEffect(() => {
    if (isLocationUpdated && !disableUpdate) {
      saveAdminEmailsNotification(adminEmails);
    }
  }, [adminEmails]);

  return (
    <div className="tab-secti flex flex-col">
      <div className="flex w-full justify-between items-center">
        <div className="tab-title">
          <Typography type="text-xl" weight="semi-bold">
            Locations Management
          </Typography>
        </div>
        <div className="flex">
          <div className="mb-1 mr-2">
            <span className="font-sans text-sm text-black-800">Select All</span>
          </div>
          <Toggle
            onClick={() => {
              selectAllLocations();
            }}
            checked={isSelectAllLocations}
            disabled={disableUpdate}
          />
        </div>
      </div>

      <div className="list-item bg-white">
        <div className="mb-3">
          <Typography type="text-md" weight="semi-bold" className="text-grey-800">
            Shop Locations
          </Typography>
        </div>

        {locations.map((location: Location, index: number) => (
          <div key={index} className="flex mb-2">
            <Checkbox
              isChecked={adminLocations.includes(location.id)}
              onChange={(isChecked) => {
                updateLocationList(location.id, isChecked);
              }}
              isDisabled={disableUpdate}
            />
            <Typography type="text-sm" weight="regular" className="text-grey-700">
              {location.name}
            </Typography>
          </div>
        ))}
      </div>

      <div className="flex w-full justify-between items-center">
        <div className="tab-title">
          <Typography type="text-xl" weight="semi-bold">
            Permissions Setup
          </Typography>
        </div>
        <div className="flex">
          <div className="mb-1 mr-2">
            <span className="font-sans text-sm text-black-800">Select All</span>
          </div>
          <Toggle
            onClick={() => {
              selectAllPermissions();
            }}
            checked={isSelectAllPermissions}
            disabled={disableUpdate}
          />
        </div>
      </div>

      <div className="card md:space-x-2">
        {permissionsGroups.map((permissionsGroup: PermissionGroup[], i: number) => (
          <div key={`groups${i}`} className="flex flex-col space-y-2 permissions-column">
            {permissionsGroup.map((elem: PermissionGroup, idx: number) => (
              <div key={idx} className="permission-group">
                <div className="mb-3">
                  <Typography type="text-md" weight="semi-bold" className="text-grey-800">
                    {elem.title}
                  </Typography>
                </div>

                {elem.permissions.map((permission: string, index: number) => (
                  <div key={idx + '-' + index} className="flex mb-2">
                    <Checkbox
                      isChecked={adminPermissions.includes(permission)}
                      onChange={(isChecked) => {
                        updatePermissionList(permission, isChecked);
                      }}
                      isDisabled={disableUpdate}
                    />
                    <Typography type="text-sm" weight="regular" className="text-grey-700">
                      {permission}
                    </Typography>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
